import type {
  ConsultationFieldDefinition,
  ConsultationTemplate,
  CreateConsultationFieldDefinitionInput,
  CreateConsultationTemplateInput,
  UpdateConsultationFieldDefinitionInput,
  UpdateConsultationTemplateInput,
} from '../types/Consultation';
import {
  type ConsultationAuthoringBundle,
  type ConsultationAuthoringImportScope,
  type ConsultationAuthoringImportSummary,
  findTemplateFieldAssignment,
  getAuthoringExportKey,
  type PortableFieldDefinition,
  type PortableTemplate,
} from './consultationAuthoringBundle';

export type ConsultationAuthoringImportDependencies = {
  listFieldDefinitions: () => Promise<ConsultationFieldDefinition[]>;
  listTemplates: () => Promise<ConsultationTemplate[]>;
  createFieldDefinition: (input: CreateConsultationFieldDefinitionInput) => Promise<ConsultationFieldDefinition | null>;
  updateFieldDefinition: (id: string, input: UpdateConsultationFieldDefinitionInput) => Promise<ConsultationFieldDefinition | null>;
  createTemplate: (input: CreateConsultationTemplateInput) => Promise<ConsultationTemplate | null>;
  updateTemplate: (id: string, input: UpdateConsultationTemplateInput) => Promise<ConsultationTemplate | null>;
  getTemplateById: (id: string) => Promise<ConsultationTemplate | null>;
  addTemplateField: (templateId: string, input: {
    fieldDefinitionId: string;
    sectionKey?: string;
    sortOrder?: number;
    isRequired?: boolean;
  }) => Promise<unknown>;
  updateTemplateField: (templateId: string, fieldDefinitionId: string, input: {
    sectionKey?: string;
    sortOrder?: number;
    isRequired?: boolean;
  }) => Promise<unknown>;
};

const toFieldDefinitionInput = (definition: PortableFieldDefinition): CreateConsultationFieldDefinitionInput => ({
  name: definition.name,
  fieldType: definition.fieldType,
  description: definition.description,
  placeholder: definition.placeholder,
  helpText: definition.helpText,
  validationJson: definition.validationJson,
  uiSchemaJson: definition.uiSchemaJson,
  schemaJson: definition.schemaJson,
  code: definition.code,
  isRequired: definition.isRequired,
  defaultValue: definition.defaultValue,
  options: definition.options,
  sortOrder: definition.sortOrder,
  isActive: definition.isActive,
});

const toTemplateInput = (template: PortableTemplate): CreateConsultationTemplateInput => ({
  name: template.name,
  code: template.code,
  domainProfileKey: template.domainProfileKey,
  subjectType: template.subjectType,
  description: template.description,
  isActive: template.isActive,
});

const importFieldDefinitions = async (
  definitions: PortableFieldDefinition[],
  dependencies: ConsultationAuthoringImportDependencies,
  existingDefinitions: ConsultationFieldDefinition[],
  summary: ConsultationAuthoringImportSummary,
) => {
  const definitionIdByKey = new Map<string, string>();

  existingDefinitions.forEach((definition) => {
    definitionIdByKey.set(getAuthoringExportKey(definition), definition.id);
  });

  for (const definition of definitions) {
    const existingId = definitionIdByKey.get(definition.exportKey);
    const input = toFieldDefinitionInput(definition);

    try {
      if (existingId) {
        const updated = await dependencies.updateFieldDefinition(existingId, input);
        if (updated) {
          definitionIdByKey.set(definition.exportKey, updated.id);
          summary.fieldDefinitions.updated += 1;
        } else {
          summary.fieldDefinitions.skipped += 1;
          summary.errors.push(`Failed to update field definition "${definition.name}".`);
        }
        continue;
      }

      const created = await dependencies.createFieldDefinition(input);
      if (created) {
        definitionIdByKey.set(definition.exportKey, created.id);
        summary.fieldDefinitions.created += 1;
      } else {
        summary.fieldDefinitions.skipped += 1;
        summary.errors.push(`Failed to create field definition "${definition.name}".`);
      }
    } catch (error) {
      summary.fieldDefinitions.skipped += 1;
      summary.errors.push(error instanceof Error
        ? `Field definition "${definition.name}": ${error.message}`
        : `Field definition "${definition.name}" failed to import.`);
    }
  }

  return definitionIdByKey;
};

const syncTemplateFields = async (
  templateId: string,
  template: PortableTemplate,
  fieldDefinitionIdByKey: Map<string, string>,
  dependencies: ConsultationAuthoringImportDependencies,
  summary: ConsultationAuthoringImportSummary,
) => {
  const detailedTemplate = await dependencies.getTemplateById(templateId);
  const existingAssignments = detailedTemplate?.fields ?? [];

  for (const assignment of template.fields) {
    const fieldDefinitionId = fieldDefinitionIdByKey.get(assignment.fieldKey);
    if (!fieldDefinitionId) {
      summary.errors.push(`Template "${template.name}" references missing field "${assignment.fieldKey}".`);
      continue;
    }

    const existingAssignment = findTemplateFieldAssignment(existingAssignments, fieldDefinitionId);
    const payload = {
      sectionKey: assignment.sectionKey,
      sortOrder: assignment.sortOrder,
      isRequired: assignment.isRequired,
    };

    try {
      if (existingAssignment) {
        await dependencies.updateTemplateField(templateId, fieldDefinitionId, payload);
      } else {
        await dependencies.addTemplateField(templateId, {
          fieldDefinitionId,
          ...payload,
        });
      }
    } catch (error) {
      summary.errors.push(error instanceof Error
        ? `Template "${template.name}" field "${assignment.fieldKey}": ${error.message}`
        : `Template "${template.name}" field "${assignment.fieldKey}" failed to import.`);
    }
  }
};

const importTemplates = async (
  templates: PortableTemplate[],
  dependencies: ConsultationAuthoringImportDependencies,
  existingTemplates: ConsultationTemplate[],
  fieldDefinitionIdByKey: Map<string, string>,
  summary: ConsultationAuthoringImportSummary,
) => {
  const templateIdByKey = new Map<string, string>();
  existingTemplates.forEach((template) => {
    templateIdByKey.set(getAuthoringExportKey(template), template.id);
  });

  for (const template of templates) {
    const existingId = templateIdByKey.get(template.exportKey);
    const input = toTemplateInput(template);

    try {
      let templateId = existingId;

      if (existingId) {
        const updated = await dependencies.updateTemplate(existingId, input);
        if (updated) {
          templateId = updated.id;
          summary.templates.updated += 1;
        } else {
          summary.templates.skipped += 1;
          summary.errors.push(`Failed to update template "${template.name}".`);
          continue;
        }
      } else {
        const created = await dependencies.createTemplate(input);
        if (created) {
          templateId = created.id;
          templateIdByKey.set(template.exportKey, created.id);
          summary.templates.created += 1;
        } else {
          summary.templates.skipped += 1;
          summary.errors.push(`Failed to create template "${template.name}".`);
          continue;
        }
      }

      if (templateId) {
        await syncTemplateFields(templateId, template, fieldDefinitionIdByKey, dependencies, summary);
      }
    } catch (error) {
      summary.templates.skipped += 1;
      summary.errors.push(error instanceof Error
        ? `Template "${template.name}": ${error.message}`
        : `Template "${template.name}" failed to import.`);
    }
  }
};

export const importConsultationAuthoringBundle = async (
  bundle: ConsultationAuthoringBundle,
  scope: ConsultationAuthoringImportScope,
  dependencies: ConsultationAuthoringImportDependencies,
): Promise<ConsultationAuthoringImportSummary> => {
  const summary: ConsultationAuthoringImportSummary = {
    fieldDefinitions: { created: 0, updated: 0, skipped: 0 },
    templates: { created: 0, updated: 0, skipped: 0 },
    errors: [],
  };

  const [existingDefinitions, existingTemplates] = await Promise.all([
    dependencies.listFieldDefinitions(),
    dependencies.listTemplates(),
  ]);

  const shouldImportFieldDefinitions = scope === 'fieldDefinitions' || bundle.fieldDefinitions.length > 0;
  const shouldImportTemplates = scope === 'templates' && bundle.templates.length > 0;

  let fieldDefinitionIdByKey = new Map<string, string>();
  existingDefinitions.forEach((definition) => {
    fieldDefinitionIdByKey.set(getAuthoringExportKey(definition), definition.id);
  });

  if (shouldImportFieldDefinitions && bundle.fieldDefinitions.length > 0) {
    fieldDefinitionIdByKey = await importFieldDefinitions(
      bundle.fieldDefinitions,
      dependencies,
      existingDefinitions,
      summary,
    );
  }

  if (shouldImportTemplates) {
    await importTemplates(
      bundle.templates,
      dependencies,
      existingTemplates,
      fieldDefinitionIdByKey,
      summary,
    );
  }

  return summary;
};

export const formatConsultationAuthoringImportSummary = (
  summary: ConsultationAuthoringImportSummary,
  scope: ConsultationAuthoringImportScope,
) => {
  const parts: string[] = [];

  if (scope === 'fieldDefinitions' || summary.fieldDefinitions.created + summary.fieldDefinitions.updated > 0) {
    parts.push(
      `Field definitions: ${summary.fieldDefinitions.created} created, ${summary.fieldDefinitions.updated} updated, ${summary.fieldDefinitions.skipped} skipped.`,
    );
  }

  if (scope === 'templates' && summary.templates.created + summary.templates.updated > 0) {
    parts.push(
      `Templates: ${summary.templates.created} created, ${summary.templates.updated} updated, ${summary.templates.skipped} skipped.`,
    );
  }

  if (parts.length === 0) {
    return 'Import completed with no changes.';
  }

  if (summary.errors.length > 0) {
    return `${parts.join(' ')} ${summary.errors.length} item(s) reported errors.`;
  }

  return parts.join(' ');
};
